import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import Swal from "sweetalert2";
import authservice from "../services/auth.services";
import { useStoreStock } from "./StoreStockContext";

export const BucketContext = createContext();

export const useBucket = () => {
  const context = useContext(BucketContext);
  if (!context) {
    throw new Error("useBucket must be used within a BucketProvider");
  }
  return context;
};

const BucketProvider = ({ children }) => {
  const userKeys = localStorage.getItem("userKey");
  const { setShowStoreStock } = useStoreStock();
  const [bucketItems, setBucketItems] = useState([]);
  const [bucketCount, setBucketCount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const totalPrice = useMemo(() => {
    return bucketItems.reduce((total, item) => {
      return total + (item.price * item.quantity || 0);
    }, 0);
  }, [bucketItems]);

  const getBucketDetails = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const userKey = options.userKey || localStorage.getItem("userKey");

      if (!userKey) {
        console.warn("User key not found. Cannot fetch basket.");
        return null;
      }
      const requestData = {
        userKey,
        ...(options.productID && { productID: options.productID }),
        ...(options.productVariationID && {
          productVariationID: options.productVariationID,
        }),
      };
      const response = await authservice.postJsonData(
        "getBucketDetails",
        requestData,
      );
      if (response?.apiResponseCode === "1001") {
        const items = response.buckets || [];
        const groupedItems = items.reduce((acc, item) => {
          const key = `${item.productID}-${item.productVariationID || "default"}`;
          if (acc[key]) {
            acc[key].quantity += 1;
            acc[key].items.push(item);
          } else {
            acc[key] = {
              ...item,
              quantity: 1,
              items: [item],
            };
          }
          return acc;
        }, {});
        const processedItems = Object.values(groupedItems);
        setBucketItems(processedItems);
        setBucketCount(items.length);
        return response;
      }
      setShowStoreStock(false);
      setBucketItems([]);
      setBucketCount(0);
      return response;
    } catch (err) {
      console.warn("Basket fetch error:", err);
      setError(err.message || "Failed to fetch basket details");
      setBucketItems([]);
      setBucketCount(0);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const addToBucket = useCallback(
    async (itemData) => {
      setLoading(true);
      setError(null);
      try {
        const requestData = {
          bucketID: itemData.bucketID || null,
          productID: itemData.productID,
          productVariationID: itemData.productVariationID,
          userKey: itemData.userKey,
          description: itemData.description,
        };
        const response = await authservice.postJsonData(
          "setBucket",
          requestData,
        );
        if (response && response.apiResponseCode === "1001") {
          await getBucketDetails();
          return response;
        }
        throw new Error(response?.message || "Failed to add item to basket");
      } catch (err) {
        console.warn("Add to basket error:", err);
        setError(err.message || "Failed to add item to basket");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getBucketDetails],
  );

  const updateBucketStatus = useCallback(
    async (productID, status) => {
      setLoading(true);
      setError(null);
      try {
        const requestData = {
          id: productID,
          status,
        };
        const response = await authservice.postJsonData(
          "updateBucketStatus",
          requestData,
        );
        if (response && response.apiResponseCode === "1001") {
          await getBucketDetails();
          return response;
        }
        throw new Error(response?.message || "Failed to update basket status");
      } catch (err) {
        console.warn("Update basket status error:", err);
        setError(err.message || "Failed to update basket status");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getBucketDetails],
  );

  const removeBucketItem = useCallback(
    async (productID) => {
      return await updateBucketStatus(productID, "DELETED");
    },
    [updateBucketStatus],
  );

  const clearBucket = async () => {
    try {
      const response = await authservice.postJsonData("clearBucketItems", {
        key: userKeys,
      });
      if (response.apiResponseCode == "1001") {
        await getBucketDetails();
        Swal.fire({
          imageUrl: "/images/clear-cart.svg",
          imageWidth: 200,
          imageHeight: 200,
          text: response.apiResponseMsg,
        });
      } else {
        Swal.fire({
          imageUrl: "/images/order-failed.svg",
          imageWidth: 200,
          imageHeight: 200,
          text: response.apiResponseMsg,
        });
      }
      return response;
    } catch (errors) {
      console.warn("Error Clearing Basket:", errors);
      Swal.fire({
        icon: "error",
        title: "Server Error",
        text: "Could not Clear Basket. Please try again later.",
      });
    }
  };

  useEffect(() => {
    const checkUserKeyAndFetch = () => {
      const userKey = localStorage.getItem("userKey");
      if (userKey) {
        getBucketDetails({ userKey });
      } else {
        setTimeout(checkUserKeyAndFetch, 500);
      }
    };
    checkUserKeyAndFetch();
  }, [getBucketDetails]);

  const contextValue = useMemo(
    () => ({
      bucketItems,
      bucketCount,
      loading,
      error,
      totalPrice,
      getBucketDetails,
      addToBucket,
      updateBucketStatus,
      removeBucketItem,
      clearBucket,
      setBucketItems,
      setBucketCount,
      setError,
    }),
    [
      bucketItems,
      bucketCount,
      loading,
      error,
      totalPrice,
      getBucketDetails,
      addToBucket,
      updateBucketStatus,
      removeBucketItem,
      clearBucket,
    ],
  );

  return (
    <BucketContext.Provider value={contextValue}>
      {children}
    </BucketContext.Provider>
  );
};

export default BucketProvider;
